import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import api from '../services/api.js';
import jwtDecode from 'jwt-decode';

const AuthContext = createContext(null);
// 59 minutes in milliseconds for the inactivity timer
const INACTIVITY_TIMEOUT_MS = 59 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Ref to hold the ID of the inactivity timer
  const inactivityTimerRef = useRef(null);

  // Memoized logout function
  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    setToken(null);
    setUser(null);
    delete api.defaults.headers.common['Authorization'];

    // Also clear the inactivity timer when logging out
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    console.log('User logged out.');
  }, []);

  // Memoized function to reset the inactivity timer
  const resetInactivityTimer = useCallback(() => {
    // Clear the existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    // Set a new timer
    inactivityTimerRef.current = setTimeout(() => {
      console.log(`User inactive for ${INACTIVITY_TIMEOUT_MS / 60000} minutes. Logging out.`);
      logout();
    }, INACTIVITY_TIMEOUT_MS);
  }, [logout]);

  // This effect sets up and tears down the inactivity event listeners
  useEffect(() => {
    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

    if (!token) {
      return;
    }

    resetInactivityTimer();

    activityEvents.forEach((event) => {
      window.addEventListener(event, resetInactivityTimer);
    });

    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      activityEvents.forEach((event) => {
        window.removeEventListener(event, resetInactivityTimer);
      });
    };
  }, [token, resetInactivityTimer]);

  // Memoized function to refresh the access token (for *active* users)
  const refreshAccessToken = useCallback(async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      console.log('No refresh token available, logging out.');
      return logout();
    }

    try {
      console.log('Attempting to refresh access token...');
      const response = await api.post('/auth/refresh', { refreshToken });

      const { access_token, refresh_token } = response.data;

      localStorage.setItem('token', access_token);
      if (refresh_token) {
        localStorage.setItem('refreshToken', refresh_token);
      }

      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      console.log('Token refreshed successfully.');

      resetInactivityTimer();
    } catch (error) {
      console.error('Failed to refresh token. Logging out.', error);
      logout();
    }
  }, [logout, resetInactivityTimer]);

  // This effect manages the *token's lifetime* (for *active* users)
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    let tokenRefreshTimerId;
    try {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const decoded = jwtDecode(token);

      if (decoded.sub && !decoded.userId) {
        decoded.userId = decoded.sub;
      }
      setUser(decoded);

      const currentTime = Date.now();
      const expirationTime = decoded.exp * 1000;
      const expiresIn = expirationTime - currentTime;

      // Schedule a refresh 5 minutes before the token expires
      const refreshTimeout = expiresIn - 5 * 60 * 1000;

      if (refreshTimeout > 0) {
        tokenRefreshTimerId = setTimeout(refreshAccessToken, refreshTimeout);
        console.log(`Token refresh scheduled in ${Math.round(refreshTimeout / 1000 / 60)} minutes.`);
      } else {
        console.log('Token expired or expiring soon, refreshing now.');
        refreshAccessToken();
      }
    } catch (error) {
      console.error('Invalid token found.', error);
      logout();
    } finally {
      setLoading(false);
    }

    return () => {
      if (tokenRefreshTimerId) {
        clearTimeout(tokenRefreshTimerId);
      }
    };
  }, [token, logout, refreshAccessToken]);

  // ✅ Login function (UPDATED): throws error instead of returning null
  const login = async (identifier, password) => {
    try {
      const response = await api.post('/auth/login', { identifier, password });
      const { access_token, refresh_token } = response.data;

      localStorage.setItem('token', access_token);
      localStorage.setItem('refreshToken', refresh_token);

      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);

      const decoded = jwtDecode(access_token);
      if (decoded.sub && !decoded.userId) {
        decoded.userId = decoded.sub;
      }
      setUser(decoded);

      resetInactivityTimer();

      return decoded;
    } catch (error) {
      // Keep axios error shape, but also ensure we always have a useful message
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Login failed. Please try again.';

      console.error('Login failed', error?.response ? error.response.data : 'No response');

      // Attach status/message for UI
      const err = new Error(typeof message === 'string' ? message : 'Login failed.');
      err.status = status;
      err.data = error?.response?.data;

      throw err; // ✅ important
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      await api.post('/auth/register', userData);
      return { success: true };
    } catch (error) {
      console.error('Registration failed', error.response ? error.response.data : 'No response');
      return { success: false, message: error.response?.data?.message || 'Registration failed' };
    }
  };

  const value = {
    user,
    token,
    isAuthenticated: !!token,
    loading,
    login,
    register,
    logout,
  };

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};
